import React, { useState, useEffect } from 'react';
import { Row, Col, Table, Select, DatePicker, Spin, Tooltip as AntTooltip, Input } from 'antd';
import { connect } from 'react-redux';
import { hasRolePO } from '../system/systemAction';
import { getPrimaryModality, getUserById } from '../studies/StudiesService';
import { getMyTeleradiologyRequests } from '../teleradiology/TeleradiologyService';
import { Filter, RotateCcw } from 'lucide-react';
import api from '../../utils/service/api';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import './AdminDashboard.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

// -- Colors --
const PRIMARY_COLOR = '#2a5f5a';
const MUTED_BLUE = '#4A90D9';
const AMBER = '#F5A623';
const SOFT_GRAY = '#6B7A8D';
const BODYPART_COLORS = ['#2a5f5a', '#397871', '#4ba198', '#6ac0b8', '#99dcd7'];
const REPORT_STATUS_COLORS = { Completed: '#2a5f5a', 'In Progress': '#F5A623', Pending: '#6B7A8D' };

// Derived series modalities — not real imaging modalities
// (modality + body part now arrive pre-derived from the /admin endpoint —
//  the client-side series-derivation tables below are no longer needed)

// Modality priority for multi-modality studies (e.g. PET/CT, MR/PET).
// Lower index = higher priority. CT/MR beat PT/NM so a PET/CT study is
// counted as CT in charts rather than as PT (whichever series happened to
// appear first in the Orthanc response).
// (see note above — modality priority is resolved server-side now)

// -- Date helpers --
const parseOrthancDate = (dateStr) => {
  if (!dateStr) return null;
  const clean = String(dateStr).replace(/[-T:]/g, '');
  const y = parseInt(clean.substring(0, 4));
  const m = parseInt(clean.substring(4, 6)) - 1;
  const d = parseInt(clean.substring(6, 8));
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  return new Date(y, m, d);
};

// Reports store timestamps as RFC3339 (e.g. "2026-01-15T10:30:00Z")
const parseReportDate = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const getMonday = (date) => {
  const d = new Date(date); const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  d.setHours(0, 0, 0, 0); return d;
};
const fmtShort = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const formatWeekLabel = (mon) => { const sun = new Date(mon); sun.setDate(sun.getDate() + 6); return `${fmtShort(mon)} – ${fmtShort(sun)}`; };
const formatMonthLabel = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

// ── Referring-physician modality palette ────────────────────────────────────
// Fixed modality → color map (validated categorical palette on the dark card
// surface — worst adjacent CVD ΔE 8.4). Color follows the modality identity,
// never its rank; segments stack in MODALITY_ORDER so adjacent segments use
// CVD-safe adjacent slots. Unknown / rare modalities fold into a neutral grey.
const MODALITY_ORDER = ['CT', 'DX', 'MR', 'US', 'MG', 'CR'];
const MODALITY_COLORS = {
  CT: '#3987e5', DX: '#d95926', MR: '#199e70',
  US: '#c98500', MG: '#d55181', CR: '#008300',
};
const MODALITY_OTHER = '#898781';
const modalityColor = (m) => MODALITY_COLORS[String(m || '').toUpperCase()] || MODALITY_OTHER;
const modalityRank = (m) => {
  const i = MODALITY_ORDER.indexOf(String(m || '').toUpperCase());
  return i === -1 ? 99 : i;
};

// Per-physician modality mix as a compact stacked bar. Identity comes from the
// per-segment hover tooltip plus the card-level legend (never colour alone); a
// 2px gap separates segments (secondary encoding for the 6–8 CVD band).
const ModalityMixBar = ({ modalities }) => {
  const segs = (modalities || [])
    .filter((m) => (m.value || 0) > 0)
    .slice()
    .sort((a, b) => modalityRank(a.name) - modalityRank(b.name));
  if (!segs.length) return <span style={{ color: '#6B7A8D' }}>—</span>;
  const sum = segs.reduce((s, m) => s + m.value, 0) || 1;
  return (
    <div style={{ display: 'flex', width: 140, height: 10 }}>
      {segs.map((m, i) => {
        const pct = (m.value / sum) * 100;
        return (
          <AntTooltip key={m.name} title={`${String(m.name).toUpperCase()}: ${m.value} (${Math.round(pct)}%)`}>
            <div style={{
              width: `${pct}%`,
              minWidth: 3,
              background: modalityColor(m.name),
              marginLeft: i === 0 ? 0 : 2,
              borderRadius: 2,
              cursor: 'default',
            }} />
          </AntTooltip>
        );
      })}
    </div>
  );
};

// Referring Physician table columns
const referringPhysicianColumns = [
  { title: 'Physician', dataIndex: 'name', key: 'name',
    render: (text) => <span style={{ fontWeight: 500, color: '#e5e7eb' }}>{text}</span>
  },
  { title: 'Referred', dataIndex: 'count', key: 'count', width: 84,
    render: (val) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{val}</span>
  },
  { title: '% Share', dataIndex: 'pct', key: 'pct', width: 76,
    render: (val) => <span style={{ color: '#9ca3af', fontVariantNumeric: 'tabular-nums' }}>{val}%</span>
  },
  { title: 'Modality Mix', dataIndex: 'modalities', key: 'modalities',
    render: (mods) => <ModalityMixBar modalities={mods} />
  },
  { title: 'Top Body Part', dataIndex: 'topBodyPart', key: 'topBodyPart', width: 124,
    render: (val) => <span style={{ color: PRIMARY_COLOR, fontWeight: 500 }}>{val || '—'}</span>
  },
];

// ── Referring-physician name normalization ──────────────────────────────────
// Collapse purely cosmetic variants of the same physician name (Dr vs Dr.,
// DICOM PN caret separators, punctuation, case, extra spaces) into one grouping
// key so the Top Referring Physicians table doesn't double-count e.g.
// "Dr Selamawi" and "Dr.Selamawi". Deterministic only — NO fuzzy spelling
// matching, so two genuinely different physicians are never merged.
const normalizePhysicianKey = (raw) =>
  String(raw || '')
    .replace(/\^/g, ' ')        // DICOM PN component separators → space
    .toLowerCase()
    .replace(/\bdr\b\.?/g, '')  // drop the "dr"/"dr." honorific
    .replace(/[.,]/g, ' ')      // punctuation → space
    .replace(/\s+/g, ' ')       // collapse whitespace
    .trim();

// Merge already-aggregated referring-physician rows by normalized key: sum the
// counts, keep the most frequently seen spelling for display, accumulate the
// FULL per-modality breakdown, and vote on the most common exam region. Returns
// ALL physicians (no top-N truncation) with each row's % share of all referrals.
const mergeReferringRows = (rows) => {
  const byKey = {};
  (rows || []).forEach((p) => {
    const name = p.name || '';
    const key = normalizePhysicianKey(name);
    if (!key) return;
    const count = p.count || 0;
    if (!byKey[key]) byKey[key] = { count: 0, variants: {}, modalities: {}, bodyParts: {} };
    const e = byKey[key];
    e.count += count;
    e.variants[name] = (e.variants[name] || 0) + count;
    // Prefer the new per-modality breakdown array; fall back to the legacy
    // single top_modality (weighted by this physician's study count).
    if (Array.isArray(p.modalities) && p.modalities.length) {
      p.modalities.forEach((m) => {
        const mn = m.name || m.key;
        const mv = m.value || m.count || 0;
        if (mn && mv > 0) e.modalities[mn] = (e.modalities[mn] || 0) + mv;
      });
    } else {
      const mod = p.top_modality || p.topModality;
      if (mod && mod !== '—') e.modalities[mod] = (e.modalities[mod] || 0) + count;
    }
    const bp = p.top_body_part || p.topBodyPart;
    if (bp) e.bodyParts[bp] = (e.bodyParts[bp] || 0) + count;
  });
  const merged = Object.values(byKey).sort((a, b) => b.count - a.count);
  const grandTotal = merged.reduce((s, e) => s + e.count, 0) || 1;
  return merged.map((e, idx) => {
    const name = Object.entries(e.variants).sort((a, b) => b[1] - a[1])[0][0];
    const modalities = Object.entries(e.modalities)
      .map(([mn, mv]) => ({ name: mn, value: mv }))
      .sort((a, b) => b.value - a.value);
    const topBody = Object.entries(e.bodyParts).sort((a, b) => b[1] - a[1])[0];
    return {
      key: String(idx + 1),
      name,
      count: e.count,
      pct: Math.round((e.count / grandTotal) * 100),
      modalities,
      topBodyPart: topBody ? topBody[0] : '—',
    };
  });
};

// Workload table columns
const workloadColumns = [
  { title: 'Radiologist', dataIndex: 'name', key: 'name',
    render: (text) => <span style={{ color: '#e5e7eb' }}>{text}</span>
  },
  { title: 'Total Assigned', dataIndex: 'assigned', key: 'assigned' },
  // Completed = studies this radiologist actually finished/signed (every completed
  // study is signed). Assignment-independent, so it also counts studies signed via
  // sign-as on the shared/admin login without an assignment.
  { title: 'Completed', dataIndex: 'signed', key: 'signed',
    render: (val) => <span style={{ color: PRIMARY_COLOR, fontWeight: 500 }}>{val ?? 0}</span>
  },
  { title: 'In Progress', dataIndex: 'inProgress', key: 'inProgress',
    render: (val) => <span style={{ color: AMBER }}>{val ?? 0}</span>
  },
  { title: 'Avg. TAT (days)', dataIndex: 'avgTAT', key: 'avgTAT',
    render: (val) => val != null ? val.toFixed(1) : '—'
  },
];

// Module-level result cache — avoids re-fetching within 45 s.
const _dashCache = {};
const CACHE_TTL = 45000;
const getCached = (key) => {
  const e = _dashCache[key];
  return e && Date.now() - e.ts < CACHE_TTL ? e.data : null;
};
const setCache = (key, data) => { _dashCache[key] = { data, ts: Date.now() }; };

// Build a radiologist's display name from a Keycloak user object — first (+last)
// name preferred, so the Workload table reads "Biniyam Mezgebo" not "bini_rimna".
const userDisplayName = (u) => {
  const full = [u.first_name || u.firstName, u.last_name || u.lastName]
    .filter(Boolean).join(' ').trim();
  return full || u.username || u.email || u.id;
};

// Display name for a user ID: first name from KC, or truncated UUID fallback.
const displayName = (id, userMap) =>
  userMap[id] || `${id.substring(0, 8)}…`;

// Teleradiology KPI counts.
//
// /teleradiology/my-requests returns EVERY request, withdrawn ones included —
// deliberately, because history views need them. But a cancelled or rejected
// study is no longer out with the network, so counting it made the card report
// withdrawn work as still in progress (9 of 9 on production when 5 were live).
//
// One helper for both call sites: they were duplicated and had already drifted
// apart by indentation, which is how the two quietly stop agreeing.
const TELE_DEAD_STATUSES = ['cancelled', 'rejected'];
const teleStatsFrom = (reqs) => {
  const live = (reqs || []).filter((r) => !TELE_DEAD_STATUSES.includes(String(r.status).toLowerCase()));
  const completed = live.filter((r) => String(r.status).toLowerCase() === 'completed').length;
  return { total: live.length, completed, active: live.length - completed };
};

const AdminDashboard = (props) => {
  const { userInfo } = props;
  const [trendRange, setTrendRange] = useState('week');
  const [loading, setLoading] = useState(true);

  const [draftFilters, setDraftFilters] = useState({ date: null, modality: [], radiologist: null });
  const [appliedFilters, setAppliedFilters] = useState({ date: null, modality: [], radiologist: null });

  const [totalStudies, setTotalStudies] = useState(0);
  const [unassignedStudies, setUnassignedStudies] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [avgTAT, setAvgTAT] = useState(null);
  const [modalityData, setModalityData] = useState([]);
  const [bodyPartData, setBodyPartData] = useState([]);
  const [reportStatus, setReportStatus] = useState({ completed: 0, inProgress: 0, pending: 0 });
  const [radiologistWorkload, setRadiologistWorkload] = useState([]);
  const [referringPhysicianData, setReferringPhysicianData] = useState([]);
  const [referringSearch, setReferringSearch] = useState('');
  const [trendData, setTrendData] = useState({ week: [], month: [], year: [] });
  const [activeUsers, setActiveUsers] = useState(0);
  // How many studies this hospital has sent to the teleradiology network, plus a
  // completed/in-progress split (from /teleradiology/my-requests).
  const [teleStats, setTeleStats] = useState({ total: 0, completed: 0, active: 0 });

  useEffect(() => {
    if (!hasRolePO(userInfo)) return;
    loadDashboard();
    const interval = setInterval(() => loadDashboard(true), 60000);
    return () => clearInterval(interval);
  }, [userInfo, appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bust the Orthanc study cache and reload immediately whenever a study is
  // uploaded from anywhere in the dashboard.  Without this, the module-level
  // _dashCache holds pre-upload Orthanc data for up to 45 s, so the Total
  // Studies KPI lags behind the All Studies table which has no client cache.
  useEffect(() => {
    const onStudyUploaded = () => {
      delete _dashCache['adminClinicalStudies'];
      loadDashboard(true);
    };
    window.addEventListener('study-uploaded', onStudyUploaded);
    window.addEventListener('project-study-uploaded', onStudyUploaded);
    return () => {
      window.removeEventListener('study-uploaded', onStudyUploaded);
      window.removeEventListener('project-study-uploaded', onStudyUploaded);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stage B: map the server-side ES aggregations response into the chart/KPI
  // state. One call replaces loading the whole corpus + per-study report fan-out.
  const applyAggregations = async (agg) => {
    let userMap = {};
    try {
      const usersRes = await api({ method: 'get', url: '/api/accounts/userinfo' });
      const raw = usersRes.data?.data || usersRes.data || [];
      const usersArray = Array.isArray(raw) ? raw : [];
      usersArray.forEach((u) => { if (u.id) userMap[u.id] = u.username || u.first_name || u.firstName || u.email || u.id; });
      setActiveUsers(usersArray.length);
    } catch (_) { /* non-fatal */ }

    setTotalStudies(agg.total || 0);
    setUnassignedStudies(agg.unassigned || 0);
    setPendingTasks(agg.pending || 0);
    setAvgTAT(agg.avg_tat != null ? parseFloat(Number(agg.avg_tat).toFixed(1)) : null);
    setReportStatus({ completed: agg.completed || 0, inProgress: agg.in_progress || 0, pending: agg.pending || 0 });

    setModalityData((agg.modality || []).slice(0, 6));

    const bp = agg.body_part || [];
    const bpTop = bp.slice(0, 5);
    const bpOthers = bp.slice(5);
    const otherSum = bpOthers.reduce((s, d) => s + (d.value || 0), 0);
    setBodyPartData(otherSum > 0 ? [...bpTop, { name: 'Other', value: otherSum, breakdown: bpOthers }] : bpTop);

    // Merge cosmetic name variants (server groups by raw ReferringPhysicianName)
    // before taking the top 10, so the same physician isn't double-counted.
    setReferringPhysicianData(mergeReferringRows(agg.referring_physician || []));

    // Merge assignment-based workload with reports-signed (keyed by the same
    // Keycloak sub). A radiologist who only signed unassigned studies (sign-as on
    // the shared login) still gets a row, with assigned=0 but signed>0.
    const signedBySub = {};
    const signerNameBySub = {};
    (agg.signed || []).forEach((s) => {
      if (!s.sub) return;
      signedBySub[s.sub] = s.count || 0;
      if (s.name) signerNameBySub[s.sub] = s.name;
    });
    // In Progress is counted the same way Completed is: by who is doing the
    // work, not by who it was assigned to. The workload bucket keys on
    // assignee_id, so a radiologist drafting a study nobody formally assigned
    // showed 0 in progress while All Studies plainly listed their drafts — and
    // on a shared hospital login that is every draft.
    const draftingBySub = {};
    (agg.drafting || []).forEach((d) => {
      if (!d.sub) return;
      draftingBySub[d.sub] = d.count || 0;
    });
    const bySub = {};
    (agg.workload || []).forEach((w) => {
      if (!w.assignee_id) return;
      bySub[w.assignee_id] = {
        sub: w.assignee_id,
        assigned: w.assigned || 0,
        completed: w.completed || 0,
        inProgress: draftingBySub[w.assignee_id] || 0,
        avgTAT: w.avg_tat != null ? parseFloat(Number(w.avg_tat).toFixed(1)) : null,
      };
    });
    // A radiologist who only signed or only drafted unassigned studies still
    // gets a row (assigned = 0).
    Object.keys(signedBySub).concat(Object.keys(draftingBySub)).forEach((sub) => {
      if (!bySub[sub]) bySub[sub] = { sub, assigned: 0, completed: 0, inProgress: draftingBySub[sub] || 0, avgTAT: null };
    });
    // Resolve display names from Keycloak (getUserById) — the SAME authoritative
    // source the studies page uses — so names are consistent (e.g. "Dr. Biniyam
    // Mezgebo") and a signer sub missing from the userinfo list still resolves.
    const nameBySub = {};
    await Promise.all(Object.keys(bySub).map(async (sub) => {
      try {
        const { data } = await getUserById(sub);
        const u = data?.data || data;
        if (u) {
          const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
          nameBySub[sub] = full || u.username || '';
        }
      } catch (_) { /* fall back below */ }
    }));
    let workloadRows = Object.values(bySub).map((r, idx) => ({
      key: String(idx + 1),
      name: nameBySub[r.sub] || userMap[r.sub] || signerNameBySub[r.sub] || displayName(r.sub, userMap),
      assigned: r.assigned, completed: r.completed, inProgress: r.inProgress,
      signed: signedBySub[r.sub] || 0,
      avgTAT: r.avgTAT,
    })).sort((a, b) => (b.signed - a.signed) || (b.assigned - a.assigned));
    if (appliedFilters.radiologist) workloadRows = workloadRows.filter((r) => r.name === appliedFilters.radiologist);
    setRadiologistWorkload(workloadRows);

    const fmtTrend = (arr, labelFn) => (arr || []).map((p) => ({
      name: labelFn(p.key), uploaded: p.uploaded || 0, reported: p.reported || 0,
      tat: p.tat != null ? parseFloat(Number(p.tat).toFixed(1)) : null,
    }));
    setTrendData({
      week:  fmtTrend(agg.trend?.week,  (k) => formatWeekLabel(new Date(k))),
      month: fmtTrend(agg.trend?.month, (k) => formatMonthLabel(new Date(k + '-01'))),
      year:  fmtTrend(agg.trend?.year,  (k) => k),
    });

    try {
      const teleRes = await getMyTeleradiologyRequests();
      const reqs = Array.isArray(teleRes?.data?.data) ? teleRes.data.data : [];
      setTeleStats(teleStatsFrom(reqs));
    } catch (_) { /* non-fatal */ }
  };

  const loadDashboard = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // Stage B fast path: server-side ES aggregations (one query). Falls through
      // to the legacy client-side path below on 409 (tenant not on the index) or
      // any error, so non-ES tenants are unaffected.
      try {
        const aggParams = {};
        const { date, modality } = appliedFilters;
        if (date && date[0] && date[1]) {
          aggParams.date_from = date[0].format('YYYYMMDD');
          aggParams.date_to = date[1].format('YYYYMMDD');
        }
        if (modality && modality.length > 0) aggParams.modality = modality.join(',');
        const aggRes = await api({ method: 'get', url: '/api/studies/admin/aggregations', params: aggParams });
        if (aggRes?.data?.data) {
          await applyAggregations(aggRes.data.data);
          setLoading(false);
          return;
        }
      } catch (e) {
        if (e?.response?.status !== 409) console.warn('[Dashboard] aggregations path failed; using legacy', e);
      }

      // ── Step 1: Four parallel calls ────────────────────────────────────────
      //
      // Study find:  returns all studies with MainDicomTags + RequestedTags.
      //   ModalitiesInStudy and ReferringPhysicianName land in RequestedTags
      //   (not MainDicomTags) — merge both when reading tags below.
      //
      // Series find: returns all series; used for per-study modality and body
      //   part since BodyPartExamined is a series-level tag, not study-level.
      //   We take the first non-derived series per study.
      // The /admin endpoint (GetClinicalStudiesExpanded) already (a) excludes
      // project-prefixed studies server-side and (b) attaches ModalitiesInStudy
      // + BodyPartExamined (first non-derived series) in RequestedTags. So we no
      // longer download the full raw study list (~0.6 MB) AND every expanded
      // series (~4.4 MB) and re-derive/filter client-side — one paginated /admin
      // call returns ~119 KB with everything the charts need, and no separate
      // project-exclusion loop is required.
      const cachedStudies = getCached('adminClinicalStudies');
      let adminStudies = [];
      if (cachedStudies) {
        adminStudies = cachedStudies;
      } else {
        let offset = 0;
        for (let page = 0; page < 100; page++) {
          const r = await api({ method: 'get', url: `/api/studies/admin?offset=${offset}&limit=1000` });
          const payload = r.data?.data || {};
          const batch = Array.isArray(payload.data) ? payload.data : [];
          adminStudies = adminStudies.concat(batch);
          if (!payload.has_more || batch.length === 0) break;
          offset += batch.length;
        }
        setCache('adminClinicalStudies', adminStudies);
      }

      // ── Step 2: Build user map (id → display name) ─────────────────────────
      let userMap = {};
      try {
        const usersRes = await api({ method: 'get', url: '/api/accounts/userinfo' });
        const raw = usersRes.data?.data || usersRes.data || [];
        const usersArray = Array.isArray(raw) ? raw : [];
        usersArray.forEach(u => {
          if (u.id) userMap[u.id] = userDisplayName(u);
        });
        setActiveUsers(usersArray.length);
      } catch (_) { /* non-fatal */ }

      // ── Step 3: Map /admin studies → clinical studies ──────────────────────
      // Modality + BodyPartExamined arrive in RequestedTags (BodyPartExamined is
      // server-computed from the first non-derived series); ReferringPhysicianName
      // is a study-level tag in MainDicomTags.
      const clinicalStudies = adminStudies.map(study => {
        const mainTags = { ...(study.MainDicomTags || {}), ...(study.RequestedTags || {}) };
        const rawStudyUID = mainTags.StudyInstanceUID || '';
        let cleanUID = rawStudyUID;
        if (rawStudyUID.match(/^[a-f0-9-]{36}\./i)) cleanUID = rawStudyUID.substring(37);

        const modality = getPrimaryModality(null, mainTags.ModalitiesInStudy || mainTags.Modality) || 'N/A';
        const bodyPart = mainTags.BodyPartExamined || 'N/A';
        const referringPhysician = mainTags.ReferringPhysicianName || '';
        const uploadDate = study.LastUpdate || mainTags.StudyDate || '';

        return { studyUID: cleanUID, rawStudyUID, modality, bodyPart, referringPhysician, uploadDate };
      });

      // ── Client-side filtering ──────────────────────────────────────────────
      const { date: dateRange, modality: filterModalities, radiologist: filterRadiologist } = appliedFilters;
      let filteredStudies = clinicalStudies;
      if (dateRange && dateRange[0] && dateRange[1]) {
        const start = dateRange[0].startOf('day').valueOf();
        const end = dateRange[1].endOf('day').valueOf();
        filteredStudies = filteredStudies.filter(s => {
          const d = parseOrthancDate(s.uploadDate);
          return d && d.getTime() >= start && d.getTime() <= end;
        });
      }
      if (filterModalities && filterModalities.length > 0) {
        filteredStudies = filteredStudies.filter(s => filterModalities.includes(s.modality));
      }
      setTotalStudies(filteredStudies.length);

      // ── Step 6: Modality & Body Part aggregation ───────────────────────────
      const modalityCounts = {};
      const bodyPartCounts = {};
      filteredStudies.forEach(s => {
        if (s.modality && s.modality !== 'N/A') modalityCounts[s.modality] = (modalityCounts[s.modality] || 0) + 1;
        if (s.bodyPart && s.bodyPart !== 'N/A') bodyPartCounts[s.bodyPart] = (bodyPartCounts[s.bodyPart] || 0) + 1;
      });
      setModalityData(Object.entries(modalityCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6));
      // Top 5 body parts + "Other" — chart is always exactly 6 bars max.
      // Everything below rank 5 is grouped into Other with its breakdown stored
      // so the tooltip can list each body part on hover.  This keeps the chart
      // clean regardless of how many distinct body parts exist across all hospitals.
      const _bpSorted = Object.entries(bodyPartCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
      const _bpTop     = _bpSorted.slice(0, 5);
      const _bpOthers  = _bpSorted.slice(5);
      const _otherSum  = _bpOthers.reduce((s, d) => s + d.value, 0);
      setBodyPartData(_otherSum > 0
        ? [..._bpTop, { name: 'Other', value: _otherSum, breakdown: _bpOthers }]
        : _bpTop);

      // ── Step 7: Fetch assignments via POST (single call, no URL limit) ────
      const studyUIDs = filteredStudies.map(s => s.studyUID).filter(Boolean);
      let assignments = {};
      try {
        const r = await api({ method: 'post', url: '/api/studies/assignments', data: { study_instance_uid: studyUIDs } });
        assignments = r.data?.data || {};
      } catch (_) { /* non-fatal — workload will show 0 assignments */ }

      // ── Step 8: Bulk report status — one POST replaces N individual /history calls ──
      // The Go endpoint fans out concurrently (16 goroutines) over the fast
      // server↔Orthanc link, cutting ~6 s of sequential browser↔server RTTs
      // down to a single ~0.5 s backend call regardless of study count.
      const reportStatuses   = {};
      const reportTimestamps = {};
      try {
        const queryUIDs = filteredStudies.map(s => s.rawStudyUID || s.studyUID);
        const r = await api({ method: 'post', url: '/api/reports/bulk-status', data: { study_instance_uids: queryUIDs } });
        const bulk = r.data?.data || {};
        filteredStudies.forEach(s => {
          const queryUID = s.rawStudyUID || s.studyUID;
          const entry = bulk[queryUID] || bulk[s.studyUID] || { status: 'none', timestamp: '' };
          reportStatuses[s.studyUID]   = entry.status    || 'none';
          reportTimestamps[s.studyUID] = entry.timestamp || '';
        });
      } catch (_) {
        filteredStudies.forEach(s => { reportStatuses[s.studyUID] = 'none'; reportTimestamps[s.studyUID] = ''; });
      }
      filteredStudies.forEach(s => {
        if (reportStatuses[s.studyUID] === undefined) reportStatuses[s.studyUID] = 'none';
      });

      // ── Step 9: Compute KPIs & workload ───────────────────────────────────
      let completedCount = 0, inProgressCount = 0, pendingReportCount = 0, unassignedCount = 0;
      const workloadMap = {}; // assigneeId → { assigned, completed, inProgress, tats[] }

      filteredStudies.forEach(s => {
        const uid       = s.studyUID;
        const rptStatus = reportStatuses[uid];
        const assigneeId = assignments[uid];
        const isAssigned = !!assigneeId;

        if (!isAssigned) unassignedCount++;
        if (rptStatus === 'submitted')      completedCount++;
        else if (rptStatus === 'draft')     inProgressCount++;
        else                               pendingReportCount++;

        if (assigneeId) {
          if (!workloadMap[assigneeId]) workloadMap[assigneeId] = { assigned: 0, completed: 0, inProgress: 0, tats: [] };
          workloadMap[assigneeId].assigned++;
          if (rptStatus === 'submitted') {
            workloadMap[assigneeId].completed++;
            const uDate = parseOrthancDate(s.uploadDate);
            const rDate = parseReportDate(reportTimestamps[uid]);
            if (uDate && rDate) {
              workloadMap[assigneeId].tats.push(Math.max(0, (rDate.getTime() - uDate.getTime()) / 86400000));
            }
          } else if (rptStatus === 'draft') {
            workloadMap[assigneeId].inProgress++;
          }
        }
      });

      setUnassignedStudies(unassignedCount);
      setPendingTasks(pendingReportCount);
      setReportStatus({ completed: completedCount, inProgress: inProgressCount, pending: pendingReportCount });

      let workloadRows = Object.entries(workloadMap).map(([id, data], idx) => ({
        key: String(idx + 1),
        name: displayName(id, userMap),
        assigned: data.assigned,
        completed: data.completed,
        inProgress: data.inProgress,
        avgTAT: data.tats.length
          ? parseFloat((data.tats.reduce((a, b) => a + b, 0) / data.tats.length).toFixed(1))
          : null,
      })).sort((a, b) => b.assigned - a.assigned);

      if (filterRadiologist) workloadRows = workloadRows.filter(r => r.name === filterRadiologist);
      setRadiologistWorkload(workloadRows);

      // ── Step 10: Referring Physician aggregation ───────────────────────────
      const physicianMap = {};
      filteredStudies.forEach(s => {
        const rawName = s.referringPhysician;
        if (!rawName) return;
        const key = normalizePhysicianKey(rawName);
        if (!key) return; // e.g. a bare "Dr" with no actual name
        if (!physicianMap[key]) physicianMap[key] = { count: 0, modalities: {}, variants: {}, bodyParts: {} };
        const entry = physicianMap[key];
        entry.count++;
        entry.variants[rawName] = (entry.variants[rawName] || 0) + 1;
        if (s.modality && s.modality !== 'N/A') {
          entry.modalities[s.modality] = (entry.modalities[s.modality] || 0) + 1;
        }
        if (s.bodyPart && s.bodyPart !== 'N/A') {
          entry.bodyParts[s.bodyPart] = (entry.bodyParts[s.bodyPart] || 0) + 1;
        }
      });
      const physicianRows = Object.values(physicianMap).sort((a, b) => b.count - a.count);
      const physicianTotal = physicianRows.reduce((sum, d) => sum + d.count, 0) || 1;
      setReferringPhysicianData(
        physicianRows.map((data, idx) => {
          // Display the most frequently seen spelling for this physician.
          const name = Object.entries(data.variants).sort((a, b) => b[1] - a[1])[0][0];
          const modalities = Object.entries(data.modalities)
            .map(([mn, mv]) => ({ name: mn, value: mv }))
            .sort((a, b) => b.value - a.value);
          const topBody = Object.entries(data.bodyParts).sort((a, b) => b[1] - a[1])[0];
          return {
            key: String(idx + 1),
            name,
            count: data.count,
            pct: Math.round((data.count / physicianTotal) * 100),
            modalities,
            topBodyPart: topBody ? topBody[0] : '—',
          };
        })
      );

      // ── Step 11: Trend data ────────────────────────────────────────────────
      const buildTrend = (keyFn, labelFn) => {
        const uploadMap = {}, reportMap = {}, tatMap = {};
        clinicalStudies.forEach(s => {
          const uDate = parseOrthancDate(s.uploadDate);
          if (!uDate) return;
          const key = keyFn(uDate);
          uploadMap[key] = (uploadMap[key] || 0) + 1;
          if (reportStatuses[s.studyUID] === 'submitted') {
            const rDate = parseReportDate(reportTimestamps[s.studyUID]) || uDate;
            const rKey = keyFn(rDate);
            reportMap[rKey] = (reportMap[rKey] || 0) + 1;
            const days = Math.max(0, (rDate.getTime() - uDate.getTime()) / 86400000);
            if (!tatMap[rKey]) tatMap[rKey] = [];
            tatMap[rKey].push(days);
          }
        });
        return [...new Set([...Object.keys(uploadMap), ...Object.keys(reportMap)])].sort()
          .map(k => {
            const tArr = tatMap[k] || [];
            const avg = tArr.length ? parseFloat((tArr.reduce((a, b) => a + b, 0) / tArr.length).toFixed(1)) : null;
            return { name: labelFn(k), uploaded: uploadMap[k] || 0, reported: reportMap[k] || 0, tat: avg };
          });
      };
      setTrendData({
        week:  buildTrend((d) => getMonday(d).toISOString().substring(0, 10), (k) => formatWeekLabel(new Date(k))),
        month: buildTrend((d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, (k) => formatMonthLabel(new Date(k + '-01'))),
        year:  buildTrend((d) => String(d.getFullYear()), (k) => k),
      });

      // Global Avg TAT
      const allTATs = [];
      filteredStudies.forEach(s => {
        if (reportStatuses[s.studyUID] === 'submitted') {
          const uDate = parseOrthancDate(s.uploadDate);
          const rDate = parseReportDate(reportTimestamps[s.studyUID]);
          if (uDate && rDate) allTATs.push(Math.max(0, (rDate.getTime() - uDate.getTime()) / 86400000));
        }
      });
      setAvgTAT(allTATs.length ? parseFloat((allTATs.reduce((a, b) => a + b, 0) / allTATs.length).toFixed(1)) : null);

      // ── Step 12: Teleradiology outsourcing count ───────────────────────────
      // How many studies this hospital has sent to the teleradiology network.
      // Independent of the study/report pipeline above, so it gets its own guard:
      // a teleradiology hiccup must never blank the rest of the board.
      try {
        const teleRes = await getMyTeleradiologyRequests();
        const reqs = Array.isArray(teleRes?.data?.data) ? teleRes.data.data : [];
        setTeleStats(teleStatsFrom(reqs));
      } catch (_) { /* non-fatal */ }

    } catch (e) {
      // Dashboard shows empty state on failure
    } finally {
      setLoading(false);
    }
  };

  if (!hasRolePO(userInfo)) return null;

  const bodyPartTotal = bodyPartData.reduce((s, d) => s + d.value, 0);
  const activeTrendData = trendData[trendRange] || [];
  const reportStatusData = [
    { name: 'Completed',   value: reportStatus.completed },
    { name: 'In Progress', value: reportStatus.inProgress },
    { name: 'Pending',     value: reportStatus.pending },
  ].filter(d => d.value > 0);
  const reportStatusTotal = reportStatusData.reduce((s, d) => s + d.value, 0);
  const modalityOptions = modalityData.map(m => m.name);

  const handleResetFilters = () => {
    const empty = { date: null, modality: [], radiologist: null };
    setDraftFilters(empty); setAppliedFilters(empty);
  };
  const handleFilterChange = (key, value) => setDraftFilters(prev => ({ ...prev, [key]: value }));
  const handleApplyFilters = () => setAppliedFilters({ ...draftFilters });

  const renderPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const { name, value } = payload[0];
    const pct = reportStatusTotal ? ((value / reportStatusTotal) * 100).toFixed(0) : 0;
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 500, color: '#111827', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <span style={{ color: REPORT_STATUS_COLORS[name] || SOFT_GRAY }}>● </span>
        {name}: <strong>{value}</strong> ({pct}%)
      </div>
    );
  };

  return (
    <div className="common-style-page admin-dashboard-page">
      <div className="dashboard-content">

        {/* -- Global Filter Bar -- */}
        <div className="global-filter-bar">
          <div className="filter-group">
            <span className="filter-label"><Filter size={14} /> Filters:</span>
            <RangePicker
              className="dark-date-picker"
              size="small"
              value={draftFilters.date}
              onChange={(v) => handleFilterChange('date', v)}
            />
            <Select
              mode="multiple" size="small" placeholder="All Modalities"
              className="dark-select" value={draftFilters.modality}
              onChange={(v) => handleFilterChange('modality', v)}
              style={{ minWidth: 160 }} maxTagCount="responsive"
            >
              {modalityOptions.map(m => <Option key={m} value={m}>{m}</Option>)}
            </Select>
            <Select
              showSearch size="small" placeholder="All Radiologists"
              className="dark-select" value={draftFilters.radiologist}
              onChange={(v) => handleFilterChange('radiologist', v)}
              style={{ width: 180 }} allowClear
            >
              {radiologistWorkload.map(r => <Option key={r.key} value={r.name}>{r.name}</Option>)}
            </Select>
          </div>
          <div className="filter-actions">
            <button className="reset-btn" onClick={handleResetFilters}><RotateCcw size={12} /> Reset</button>
            <button className="apply-btn" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>

        <Spin spinning={loading}>

        {/* -- Row 1: KPI Cards -- */}
        {/* flex="1 1 160px": five equal cards on wide screens, wrapping cleanly
            to 2-up on small screens (basis 160px) — no /24 divisibility issue. */}
        <Row gutter={[10, 10]}>
          <Col flex="1 1 160px">
            <div className="dashboard-card summary-card">
              <div className="card-title">Total Studies</div>
              <div className="card-value">{totalStudies.toLocaleString()}</div>
            </div>
          </Col>
          <Col flex="1 1 160px">
            <div className={`dashboard-card summary-card${unassignedStudies > 5 ? ' warning-red' : ''}`}>
              <div className="card-title">Unassigned Studies</div>
              <div className="card-value">{unassignedStudies.toLocaleString()}</div>
              {unassignedStudies > 5 && <div className="card-desc warning-text">Needs attention</div>}
            </div>
          </Col>
          <Col flex="1 1 160px">
            <div className="dashboard-card summary-card">
              <div className="card-title">Pending Reports</div>
              <div className="card-value">{pendingTasks.toLocaleString()}</div>
            </div>
          </Col>
          <Col flex="1 1 160px">
            <div className={`dashboard-card summary-card${avgTAT != null && avgTAT > 5 ? ' warning-yellow' : ''}`}>
              <div className="card-title">Avg. TAT (days)</div>
              <div className="card-value">{avgTAT != null ? avgTAT.toFixed(1) : '—'}</div>
              {avgTAT != null && avgTAT > 5 && <div className="card-desc warning-text">Above 5-day target</div>}
            </div>
          </Col>
          <Col flex="1 1 160px">
            <div className="dashboard-card summary-card">
              <div className="card-title">Sent to Teleradiology</div>
              <div className="card-value">{teleStats.total.toLocaleString()}</div>
              {teleStats.total > 0 && (
                <div className="card-desc">{teleStats.completed} completed · {teleStats.active} in progress</div>
              )}
            </div>
          </Col>
        </Row>

        {/* -- Row 2: Report Status + Modality + Body Part -- */}
        <Row gutter={[10, 10]} style={{ marginTop: 8 }}>
          <Col xs={24} lg={8}>
            <div className="dashboard-card chart-card">
              <div className="card-header"><h3>Report Status</h3></div>
              <div className="chart-container" style={{ height: 195 }}>
                {reportStatusTotal > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={reportStatusData}
                        cx="50%" cy="42%"
                        innerRadius={45} outerRadius={68}
                        paddingAngle={3} dataKey="value" stroke="none"
                      >
                        {reportStatusData.map((entry, i) => (
                          <Cell key={i} fill={REPORT_STATUS_COLORS[entry.name] || SOFT_GRAY} />
                        ))}
                      </Pie>
                      <Tooltip content={renderPieTooltip} />
                      <Legend
                        iconType="circle" iconSize={8}
                        formatter={(value, entry) => {
                          const pct = reportStatusTotal ? ((entry.payload.value / reportStatusTotal) * 100).toFixed(0) : 0;
                          return <span style={{ color: '#d1d5db', fontSize: 11 }}>{value} ({pct}%)</span>;
                        }}
                        wrapperStyle={{ paddingTop: 8, fontSize: 11 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7A8D' }}>No data</div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="dashboard-card chart-card">
              <div className="card-header"><h3>Study Volume by Modality</h3></div>
              <div className="chart-container" style={{ height: 195 }}>
                {modalityData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={modalityData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                      <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fill: '#d1d5db', fontSize: 10 }} />
                      <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 10 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', color: '#111827', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                        formatter={(v) => [v, 'Studies']}
                        cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                      />
                      <Bar dataKey="value" fill={PRIMARY_COLOR} radius={[4, 4, 0, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7A8D' }}>No data</div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="dashboard-card chart-card">
              <div className="card-header"><h3>Study Volume by Body Part</h3></div>
              <div className="chart-container" style={{ height: 195 }}>
                {bodyPartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={bodyPartData} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                      <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 10 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" stroke="#9CA3AF" tick={{ fill: '#d1d5db', fontSize: 10 }} width={90} />
                      <Tooltip
                        cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const { name, value, breakdown } = payload[0].payload;
                          const pct = bodyPartTotal ? ((value / bodyPartTotal) * 100).toFixed(0) : 0;
                          return (
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', color: '#111827', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxWidth: 220 }}>
                              <div>{name}: {value} ({pct}%)</div>
                              {breakdown && (
                                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #e5e7eb' }}>
                                  {breakdown.map(d => (
                                    <div key={d.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#6B7280', fontSize: 11, fontWeight: 400 }}>
                                      <span>{d.name}</span><span>{d.value}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>
                        {bodyPartData.map((_, i) => <Cell key={i} fill={BODYPART_COLORS[i % BODYPART_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7A8D' }}>No data</div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* -- Row 3: Radiologist Workload + Top Referring Physicians -- */}
        <Row gutter={[10, 10]} style={{ marginTop: 8 }}>
          <Col xs={24} lg={12}>
            <div className="dashboard-card table-card">
              <div className="card-header"><h3>Radiologist Workload</h3></div>
              <Table
                dataSource={radiologistWorkload} columns={workloadColumns}
                pagination={false} className="dark-theme-table compact-table" size="small"
                locale={{ emptyText: <span style={{ color: '#6B7A8D' }}>No assignments yet</span> }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <div className="dashboard-card table-card">
              <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <h3 style={{ margin: 0 }}>Referring Physicians</h3>
                <Input
                  allowClear
                  size="small"
                  placeholder="Search physician…"
                  value={referringSearch}
                  onChange={(e) => setReferringSearch(e.target.value)}
                  style={{ maxWidth: 200 }}
                />
              </div>
              {/* Modality legend — identity is never carried by colour alone. */}
              {(() => {
                const present = [];
                const seen = {};
                referringPhysicianData.forEach((r) => (r.modalities || []).forEach((m) => {
                  const u = String(m.name || '').toUpperCase();
                  if (u && !seen[u]) { seen[u] = 1; present.push(u); }
                }));
                present.sort((a, b) => modalityRank(a) - modalityRank(b));
                if (!present.length) return null;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', padding: '2px 0 10px' }}>
                    {present.map((m) => (
                      <span key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#9ca3af' }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: modalityColor(m), display: 'inline-block' }} />
                        {m}
                      </span>
                    ))}
                  </div>
                );
              })()}
              <Table
                dataSource={(() => {
                  const q = referringSearch.trim().toLowerCase();
                  return q
                    ? referringPhysicianData.filter((r) => String(r.name || '').toLowerCase().includes(q))
                    : referringPhysicianData;
                })()}
                columns={referringPhysicianColumns}
                pagination={false} className="dark-theme-table compact-table" size="small"
                scroll={{ y: 264 }} sticky
                locale={{ emptyText: <span style={{ color: '#6B7A8D' }}>No referring physician data</span> }}
              />
            </div>
          </Col>
        </Row>

        {/* -- Row 4: Completion Trend -- */}
        <div className="trend-section">
        <Row gutter={[10, 10]}>
          <Col span={24}>
            <div className="dashboard-card chart-card">
              <div className="card-header">
                <h3>Completion Trend</h3>
                <div className="trend-toggle">
                  {['week', 'month', 'year'].map((key) => (
                    <button key={key} className={trendRange === key ? 'active' : ''} onClick={() => setTrendRange(key)}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="chart-container">
                {activeTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                    <ComposedChart data={activeTrendData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                      <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                      <YAxis yAxisId="left"  stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 10 }} allowDecimals={false} label={{ value: 'Studies', angle: -90, position: 'insideLeft', fill: '#6B7A8D', fontSize: 11, dy: 20 }} />
                      <YAxis yAxisId="right" orientation="right" stroke={AMBER} tick={{ fill: AMBER, fontSize: 10 }} label={{ value: 'Avg TAT (days)', angle: 90, position: 'insideRight', fill: AMBER, fontSize: 11, dy: -30 }} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', color: '#111827', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                        itemStyle={{ color: '#111827' }}
                        labelStyle={{ color: '#6B7280', fontWeight: 600, marginBottom: 2 }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: '8px', fontSize: '11px' }} />
                      <Line yAxisId="left"  type="monotone" name="Total Uploaded" dataKey="uploaded" stroke={PRIMARY_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="left"  type="monotone" name="Total Reported" dataKey="reported" stroke={MUTED_BLUE}   strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="right" type="monotone" name="Avg TAT (days)" dataKey="tat"      stroke={AMBER}        strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7A8D' }}>No trend data available</div>
                )}
              </div>
            </div>
          </Col>
        </Row>
        </div>

        </Spin>
      </div>
    </div>
  );
};

export default connect(
  (state) => ({ userInfo: state.system.profile }),
  null
)(AdminDashboard);
